import { onAuthStateChanged } from 'firebase/auth';
import { authDe, idsDesPortees } from './portees.js';
import {
  type CollectionReference,
  type DocumentSnapshot,
  type QuerySnapshot,
  doc,
  limit as limitTo,
  onSnapshot,
  query,
} from 'firebase/firestore';
import {
  MAX_LIST_LIMIT,
  buildListQuery,
  enrichIndexError,
  planListQuery,
  type CadrageAcces,
  type ListOptions,
} from './list-query.js';
import { instantaneDuServeur } from './pastilles.js';

/**
 * OBSERVER UNE FICHE : la lecture en direct d'un document, ecrite UNE fois pour
 * les familles qui en ont besoin, sur le modele de `archive-ops.ts`.
 *
 * POURQUOI ELLE EXISTE. Jusqu'ici le SDK ne savait lire qu'en une fois (`get`,
 * `list`) : un ecran qui voulait voir arriver le travail d'un collegue devait
 * recharger, ou importer `firebase/firestore` lui-meme, ce que la regle d'or 1
 * interdit. Le premier besoin reel est celui des moteurs tiers (tableau blanc,
 * carte mentale) : deux personnes ouvertes sur le meme contenu doivent voir le
 * travail de l'autre arriver sans recharger. Le socle avait deja trois lectures
 * en direct (`members.me`, `branding`, `appsInstanciees.observer`) : celle-ci
 * est la meme mecanique, rendue a une famille.
 *
 * BRANCHEE SUR TOUTES LES FAMILLES, par `createArchiveOps` (archive-ops.ts),
 * la fabrique que chaque repository etale deja : `sdk.<famille>.observe`, et
 * `sdk.famille(nom).observe` qui le relaie. Le premier besoin commun est la
 * fiche ouverte d'un ecran declare, qui doit voir arriver le numero ou le
 * calcul que le serveur ecrit apres elle.
 *
 * CE QUE LE RAPPEL RECOIT, et pourquoi deux choses :
 *  - `record` : la fiche validee par le schema de la famille, ou `null` si le
 *    document n'existe pas et que les rules le laissent dire. Pour `contents`
 *    (comme pour toute famille dont la lecture passe par `peutLire`), un
 *    document absent est un REFUS des rules : l'abonne recoit alors `onError`
 *    (`permission-denied`), jamais `null` ;
 *  - `local` : VRAI quand ce rendu vient d'une ecriture de CE client pas encore
 *    confirmee par le serveur (`hasPendingWrites`). Firestore rejoue a chaque
 *    abonne ses propres ecritures avant de les envoyer ; un ecran qui vient
 *    d'enregistrer n'a rien a apprendre de son propre echo, et il le saute.
 *
 * UNE FICHE ILLISIBLE NE TUE PAS L'ABONNEMENT : un document qui ne passe pas le
 * schema est remonte par `onError`, et l'ecoute continue. Le lecteur decide.
 */
export interface ObserveOps<T> {
  /**
   * S'abonne a une fiche. Rend la fonction qui se desabonne.
   * @param onError - erreur reseau, refus des rules, ou fiche illisible.
   */
  observe(
    id: string,
    onChange: (record: T | null, meta: { local: boolean }) => void,
    onError?: (error: unknown) => void,
  ): () => void;
}

/**
 * Ce que le rappel recoit pour un instantane donne. Pure, exportee pour etre
 * prouvee sans Firestore : un instantane absent rend `null`, un instantane
 * present rend la fiche que le converter de la collection a validee, et
 * `local` recopie `hasPendingWrites`.
 */
export function lectureDeSnapshot<T>(snap: {
  exists(): boolean;
  data(): T | undefined;
  metadata: { hasPendingWrites: boolean };
}): { record: T | null; local: boolean } {
  const local = snap.metadata.hasPendingWrites;
  if (!snap.exists()) return { record: null, local };
  const record = snap.data();
  return { record: record === undefined ? null : record, local };
}

/**
 * @param col - la collection AVEC son converter : c'est lui qui valide la fiche
 *   (`createConverter`), exactement comme pour `get`.
 */
export function createObserveOps<T>(col: CollectionReference<T>): ObserveOps<T> {
  return {
    observe(id, onChange, onError) {
      return onSnapshot(
        doc(col, id),
        (snap: DocumentSnapshot<T>) => {
          try {
            const { record, local } = lectureDeSnapshot<T>(snap);
            onChange(record, { local });
          } catch (cause) {
            // Le converter a refuse la fiche : on le dit, on ne coupe pas.
            onError?.(cause);
          }
        },
        (cause) => {
          onError?.(cause);
        },
      );
    },
  };
}

/**
 * CE QU'UN RENDU DE LISTE DIT DE SA PROVENANCE :
 *  - `local` : il porte une ecriture de CE client pas encore confirmee
 *    (`hasPendingWrites`) ;
 *  - `serveur` : le serveur l'a vu (`instantaneDuServeur`, le critere de la
 *    pastille) : ni cache, ni ecriture en attente. Un `count()` serveur
 *    relance sur un rendu qui ne l'est pas rate l'ecriture qui l'a produit
 *    (le cercle du suivi fige apres le premier message, recette du 27/10).
 *    Un rendu vide de remise a zero (session, erreur) n'est pas serveur.
 */
export interface MetaDeListe {
  local: boolean;
  serveur: boolean;
}

/** Une liste vivante porte exactement le cadrage et les options de `list`. */
export interface ObserveListOps<T> {
  observeList(
    options: ListOptions,
    onChange: (records: T[], meta: MetaDeListe) => void,
    onError?: (error: unknown) => void,
  ): () => void;
}

/** Une page d'archives trop remplie de suppressions pour une ecoute bornee. */
export class ObserveListWindowError extends Error {
  constructor() {
    super('observeList : la fenêtre maximale contient trop de fiches supprimées. Changez de page.');
    this.name = 'ObserveListWindowError';
  }
}

/** Elargit la fenetre par paliers geometriques sans depasser le plafond SDK. */
export function nextObserveListLimit(current: number): number | null {
  return current >= MAX_LIST_LIMIT ? null : Math.min(MAX_LIST_LIMIT, current * 2);
}

/** Projection pure d'un instantane de requete, avec la meme visibilite que `list`. */
export function lectureDeListeSnapshot<T>(
  snap: { docs: Array<{ data(): T }>; metadata: { hasPendingWrites: boolean; fromCache?: boolean } },
  archived: 'exclude' | 'only' | 'all',
  requestedLimit: number | null,
): { records: T[] } & MetaDeListe {
  const rows = snap.docs.map((doc) => doc.data());
  const visible = archived === 'exclude'
    ? rows
    : rows.filter((row) => (row as { deletedAt?: unknown } | null)?.deletedAt == null);
  return {
    records: requestedLimit === null ? visible : visible.slice(0, requestedLimit),
    local: snap.metadata.hasPendingWrites,
    serveur: instantaneDuServeur(snap.metadata),
  };
}

/**
 * Faut-il refaire une liste vivante ? Pure, exportee pour etre prouvee sans
 * Firestore. `avant` est l'etat sous lequel la requete courante a ete
 * construite (l'uid de la session, `null` sans session) ou le dernier etat vu
 * d'une metadonnee ; `undefined` quand aucun etat n'a encore ete vu, c'est le
 * premier instantane, qui decrit l'etat deja pris en compte. Seul un VRAI
 * changement refait : se desabonner puis se reabonner a la meme requete
 * pendant que le serveur la refuse tue le client Firestore de la page
 * (`docs/socle/connexion-membre.md`).
 */
export function vraiChangement(avant: string | null | undefined, apres: string | null): boolean {
  return avant !== undefined && avant !== apres;
}

/** L'etat d'une metadonnee suivie : son contenu, `null` si le document est absent. */
function etatDe(snap: DocumentSnapshot): string | null {
  return snap.exists() ? JSON.stringify(snap.data()) : null;
}

/**
 * Construit une seule requete de liste pour `list` et `observeList`. Le montage
 * est asynchrone car `after` relit son document avec les droits de l'appelant.
 * Le retour reste un desabonnement immediat : il annule aussi ce montage.
 *
 * En modes archives, les fiches supprimees sont filtrees cote client comme
 * dans `list`. On elargit la fenetre serveur si elles occupent les places de
 * la page visible. Au plafond du SDK, on signale une erreur au lieu de rendre
 * une page tronquee ou d'ouvrir une ecoute illimitee.
 */
export function createObserveListOps<T>(
  col: CollectionReference<T>,
  orgId: string,
  /**
   * Le cadrage d'une famille a acces par fiche (`cadrageAcces`, comme `list`) :
   * sans lui, une ecoute de `messages` ne pose pas `acces.visibilite` et les
   * rules la refusent. Absent pour les autres familles.
   */
  cadrage?: (options: ListOptions) => Promise<CadrageAcces>,
): ObserveListOps<T> {
  return {
    observeList(options, onChange, onError) {
      const stableOptions: ListOptions = {
        ...options,
        where: options.where === undefined ? undefined : { ...options.where },
        orderBy: typeof options.orderBy === 'object' && options.orderBy !== null
          ? { ...options.orderBy }
          : options.orderBy,
      };
      // La fenetre (limite, archives) ne depend pas de l'acces : elle se calcule
      // tout de suite. Le plan complet, cadrage compris, suit le montage.
      let plan = planListQuery(orgId, cadrage === undefined ? stableOptions : { ...stableOptions, acces: undefined });
      // Une app sans Auth (sans cle API) n'a pas de session qui change : rien a suivre.
      const auth = authDe(col.firestore.app);
      // La session sous laquelle la requete courante est construite.
      let uidDeLaRequete = auth?.currentUser?.uid ?? null;
      // Le curseur est relu une seule fois. Une fiche qui bouge ensuite ne
      // deplace pas la fenetre lors de son elargissement.
      const construireBase = async () => {
        if (cadrage === undefined) return buildListQuery(col, orgId, { ...stableOptions, limit: undefined });
        const cadre = await cadrage(stableOptions);
        plan = planListQuery(orgId, stableOptions, cadre);
        return buildListQuery(col, orgId, { ...stableOptions, limit: undefined }, cadre);
      };
      let baseQuery = construireBase();
      let stopped = false;
      let generation = 0;
      let unsubscribe: (() => void) | undefined;
      let serverLimit = plan.limit;

      const fail = (cause: unknown) => {
        if (!stopped) onError?.(enrichIndexError(col.id, plan, cause));
      };

      const listen = async () => {
        const ownGeneration = ++generation;
        const requestedServerLimit = serverLimit;
        try {
          const base = await baseQuery;
          if (stopped || ownGeneration !== generation) return;
          const q = requestedServerLimit === null ? base : query(base, limitTo(requestedServerLimit));
          unsubscribe?.();
          unsubscribe = onSnapshot(
            q,
            { includeMetadataChanges: true },
            (snap: QuerySnapshot<T>) => {
              if (stopped || ownGeneration !== generation) return;
              try {
                const { records, local, serveur } = lectureDeListeSnapshot(snap, plan.archived, plan.limit);
                if (
                  plan.archived !== 'exclude' &&
                  plan.limit !== null &&
                  requestedServerLimit !== null &&
                  records.length < plan.limit &&
                  snap.size === requestedServerLimit
                ) {
                  const nextLimit = nextObserveListLimit(requestedServerLimit);
                  if (nextLimit === null) {
                    fail(new ObserveListWindowError());
                    unsubscribe?.();
                    unsubscribe = undefined;
                    return;
                  }
                  serverLimit = nextLimit;
                  void listen();
                  return;
                }
                onChange(records, { local, serveur });
              } catch (cause) {
                // Un converter peut refuser une fiche. L'ecoute reste ouverte
                // pour qu'une correction distante produise un nouveau snapshot.
                fail(cause);
              }
            },
            (cause) => {
              if (stopped || ownGeneration !== generation) return;
              unsubscribe = undefined;
              onChange([], { local: false, serveur: false });
              fail(cause);
            },
          );
        } catch (cause) {
          if (!stopped && ownGeneration === generation) {
            unsubscribe?.();
            unsubscribe = undefined;
            fail(cause);
          }
        }
      };

      void listen();
      // Une nouvelle session ou une publication efface d'abord l'ancien resultat.
      // Seulement un VRAI changement (`vraiChangement`) : le premier rappel de
      // session et le premier instantane d'une metadonnee ne changent rien.
      const metadataStops: (() => void)[] = [];
      const refaire = () => {
        if (stopped) return;
        unsubscribe?.();
        onChange([], { local: false, serveur: false });
        baseQuery = construireBase();
        void listen();
      };
      const metadataFail = (cause: unknown) => {
        if (stopped) return;
        generation++; unsubscribe?.(); unsubscribe = undefined;
        onChange([], { local: false, serveur: false }); fail(cause);
      };
      const suivre = (chemin: string[]) => {
        let vu: string | null | undefined;
        metadataStops.push(onSnapshot(doc(col.firestore, 'orgs', orgId, ...chemin), snap => {
          const etat = etatDe(snap);
          const change = vraiChangement(vu, etat);
          vu = etat;
          if (change) refaire();
        }, metadataFail));
      };
      const arretSession = auth === null ? () => {} : onAuthStateChanged(auth, user => {
        metadataStops.splice(0).forEach(stop => stop());
        if (user) {
          for (const id of idsDesPortees(col.id)) suivre(['portees', id]);
          if (user.email) suivre(['members', user.email.toLowerCase()]);
        }
        const uid = user?.uid ?? null;
        const change = vraiChangement(uidDeLaRequete, uid);
        uidDeLaRequete = uid;
        if (change) refaire();
      });
      return () => {
        arretSession();
        metadataStops.forEach(stop => stop());
        stopped = true;
        generation += 1;
        unsubscribe?.();
        unsubscribe = undefined;
      };
    },
  };
}
